import React, { useEffect, useRef, useState, CSSProperties } from 'react';
import styled from 'styled-components';
import CirclePlayIcon from '@src/svgs/CirclePlayIcon';
import MuxPlayer from '@mux/mux-player-react';
import { useVideoPlayer } from '../hooks/VideoPlayerContext';

const PauseIcon = () => (
  <svg viewBox="0 0 24 24" width="24" height="24" fill="#F2D093">
    <path d="M14,19H18V5H14M6,19H10V5H6V19Z" />
  </svg>
);

// Custom player style for the preview
const playerStyle = {
  width: '100%',
  height: '100%',
  overflow: 'hidden',
  border: '0px solid #484848',
  aspectRatio: 16 / 9,
  '--pip-button': 'none',
  '--center-controls': 'none',
  '--controls': 'none',
} as CSSProperties;


/**
 * VideoPlayerPreview component provides a video preview area with playback controls
 * that integrates with the existing video player context.
 */
const VideoPlayerPreview = () => {
  const videoPlayerAPI = useVideoPlayer();
  const muxPlayerRef = useRef<any>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // Connect the MuxPlayer ref to the context's videoPlayerRef
  useEffect(() => {
    if (videoPlayerAPI && muxPlayerRef.current) {
      // Set the video reference directly in the context
      videoPlayerAPI.setVideoRef(muxPlayerRef.current);
    }
  }, [muxPlayerRef.current, videoPlayerAPI]);

  // Handle play/pause toggle - directly use MuxPlayer's API
  const handlePlayPause = () => {
    const newPlayingState = !isPlaying;
    setIsPlaying(newPlayingState);

    // Use the MuxPlayer directly for playback control
    if (newPlayingState) {
      if (muxPlayerRef.current) {
        const playPromise = muxPlayerRef.current.play();
        if (playPromise !== undefined) {
          playPromise
            .then(() => {
              console.log('Video playback started successfully');
            })
            .catch((error: any) => {
              console.error('Error playing video:', error);
              // Reset UI state if playback fails
              setIsPlaying(false);
            });
        }
      }
    } else {
      if (muxPlayerRef.current) {
        muxPlayerRef.current.pause();
      }
    }
  };

  // Listen for play/pause events from the video to update local state
  useEffect(() => {
    // Setup listeners to sync with player state
    const handlePlay = () => setIsPlaying(true);
    const handlePause = () => setIsPlaying(false);

    // Get the MuxPlayer's media element
    // @ts-ignore - Accessing the mediaElement of MuxPlayer
    const mediaElement = muxPlayerRef.current?.mediaElement;

    if (mediaElement) {
      // Initial state check - only if we have a media element
      setIsPlaying(!videoPlayerAPI.isPaused());

      // Add event listeners
      mediaElement.addEventListener('play', handlePlay);
      mediaElement.addEventListener('pause', handlePause);

      // Add loaded metadata event to ensure video is ready
      mediaElement.addEventListener('loadedmetadata', () => {
        console.log('Video metadata loaded');
        setIsLoading(false);
      });

      // Also handle loading failures
      mediaElement.addEventListener('error', () => {
        console.error('Video loading error');
        setIsLoading(false);
      });

      return () => {
        mediaElement.removeEventListener('play', handlePlay);
        mediaElement.removeEventListener('pause', handlePause);
        mediaElement.removeEventListener('loadedmetadata', () => setIsLoading(false));
        mediaElement.removeEventListener('error', () => setIsLoading(false));
      };
    }
  }, [muxPlayerRef.current, videoPlayerAPI]);

  // Determine if we have a valid video source
  const hasVideoSource = !!(videoPlayerAPI.getVideoUrl());

  return (
    <VideoPlayerContainer>
      <PanelHeader>
        <SectionTitle>Video</SectionTitle>
      </PanelHeader>

      {hasVideoSource ? (
        <VideoWrapper>
          <VideoPreviewContainer>
            <MuxPlayer
              ref={muxPlayerRef}
              style={playerStyle}
              streamType="on-demand"
              src={videoPlayerAPI.getVideoUrl()}
              preload="auto"
              muted={false}
              crossOrigin="anonymous"
            />

            <PlayPauseOverlay onClick={handlePlayPause}>
              {isPlaying ? (
                <PauseIcon />
              ) : (
                <CirclePlayIcon />
              )}
            </PlayPauseOverlay>
          </VideoPreviewContainer>
        </VideoWrapper>
      ) : (
        <NoVideoMessage>
          No video available for preview
        </NoVideoMessage>
      )}
    </VideoPlayerContainer>
  );
};

// Styled Components
const VideoPlayerContainer = styled.div`
  grid-area: video;
  display: flex;
  flex-direction: column;
  height: 300px;
  min-height: 300px;
  border-left: 1px solid #1a1e23;
  border-top: 1px solid #1a1e23;
  border-right: 1px solid #1a1e23;
  justify-content: flex-end;
`;

const PanelHeader = styled.div`
  display: flex;
  flex-direction: column;
  padding: 16px 16px 8px;
  border-bottom: 1px solid #1a1e23;
`;

const SectionTitle = styled.h2`
  font-size: 16px;
  font-weight: 600;
  margin: 0;
  color: #FFFFFF;
`;

const VideoWrapper = styled.div`
  height: calc(100% - 60px);
  display: flex;
  flex-direction: column;
`;

const VideoPreviewContainer = styled.div`
  flex: 1;
  position: relative;
  overflow: hidden;
  background-color: #000000;
`;

const PlayPauseOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: rgba(0, 0, 0, 0.3);
  opacity: 0;
  transition: opacity 0.2s ease;
  cursor: pointer;
  z-index: 1;

  &:hover {
    opacity: 1;
  }
`;

const TransportControls = styled.div`
  display: flex;
  justify-content: center;
  gap: 16px;
  margin-top: 16px;
`;

const TransportButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  background-color: rgba(255, 255, 255, 0.1);
  border: none;
  color: #FFFFFF;
  padding: 8px 16px;
  border-radius: 4px;
  cursor: pointer;

  &:hover {
    background-color: rgba(255, 255, 255, 0.2);
  }
`;

const NoVideoMessage = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: #A0A6AE;
  font-style: italic;
  padding: 16px;
`;

export default VideoPlayerPreview;